import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.css';
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Redirect, Route, Router, Switch, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useLocalStorageState from 'use-local-storage-state';
import whyDidYouRender from './debug/Debug';
import AddUser from './pages/ControlPanel/AddUser';
import AlertParameters from './pages/ControlPanel/AlertParameters';
import ControlPanel from './pages/ControlPanel/ControlPanel';
import CrAlertEmails from './pages/ControlPanel/CrAlertEmails';
import CustomerReportDates from './pages/ControlPanel/CustomerReportDates';
import CustomerView from './pages/ControlPanel/CustomerView';
import DiseasesList from './pages/ControlPanel/DiseasesList';
import ManageUsers from './pages/ControlPanel/ManageUsers';
import SendPushMessages from './pages/ControlPanel/SendPushMessages';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import GenericCrAlertList from './pages/GenericCrAlertList/GenericCrAlertList';
import Login from './pages/login/Login';
import TwoFactorSetup from './pages/login/TwoFactorSetup';
import TwoFactorSecretKey from './pages/login/TwoFactorSecretKey';
import TwoFactorVerify from './pages/login/TwoFactorVerify';
import TechFormDetail from './pages/TechFormDetail/TechFormDetail';
import ResourceList from './pages/ResourceList/ResourceList';
import ResourceForm from './pages/ResourceList/ResourceForm';
import TechFormList from './pages/TechFormList/TechFormList';
import VisitEditing from './pages/Visits/Editing/VisitEditing';
import VisitList from './pages/Visits/List/VisitList';
import { CAC_TYPE, HTTP_CODE, SERVER_CODE_MAPPER } from './res/Constants';
import './res/styles/common.scss';
import './res/styles/util_classes.scss';
import { isCurrentUserAdmin, isUserLogged, logout } from './service/AccountService';
import './service/AxiosConfig';
import { CAC_TYPE_STORAGE } from './service/CacTypeService';
import { isProduction } from './service/InfoService';
import { toastError } from './ui/ToastHelper';

const OnlyNotLoggedRoute = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) =>
            !isUserLogged() ? <Component {...props} /> : <Redirect to='/techforms' />
        }
    />
);

const PrivateRoute = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) => (isUserLogged() ? <Component {...props} /> : <Redirect to='/' />)}
    />
);

const AdminRoute = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) => (isCurrentUserAdmin() ? <Component {...props} /> : <Redirect to='/' />)}
    />
);

const getErrorText = (errorCode) =>
    SERVER_CODE_MAPPER[errorCode] != null ? SERVER_CODE_MAPPER[errorCode] : `Error: ${errorCode}`;

if (!isProduction) {
    whyDidYouRender(React);
}
toast.configure();

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1
        }
    }
});

const AppContext = React.createContext();
export { AppContext };

export const DEFAULT_FILTERS_BY_USER = {
    crop: null,
    nursery: null
};

const clearCache = () => {
    queryClient.clear();
};

function App(props) {
    const [cacType, setCacType] = useLocalStorageState(CAC_TYPE_STORAGE, {
        defaultValue: CAC_TYPE.CROP
    });
    const [filtersByUser, setFiltersByUser] = React.useState(DEFAULT_FILTERS_BY_USER);

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const errorStatus =
                error.response == null ? HTTP_CODE.SERVER_ERROR : error.response.status;
            const errorType = error.response?.data?.type;

            switch (errorStatus) {
                case HTTP_CODE.UNAUTHORIZED:
                    // Skip interceptor for 2FA-related errors, let them be handled by login callback
                    if (errorType === '/errors/authentication/tfaNotEnabled' ||
                        errorType === '/errors/authentication/otpMissing' ||
                        errorType === '/errors/authentication/badCredentials') {
                        return Promise.reject(error);
                    }

                    // Normal unauthorized error handling
                    logout();
                    clearCache();
                    props.history.push('/login');
                    toastError(getErrorText(error.response.status));
                    break;
                case HTTP_CODE.SERVER_ERROR:
                case HTTP_CODE.NOT_FOUND:
                    if (!window.location.href.includes('/error')) {
                        props.history.push({
                            pathname: '/error',
                            state: {
                                errorCode: errorStatus,
                                errorText: getErrorText(HTTP_CODE.SERVER_ERROR)
                            }
                        });
                    }
                    break;
                default:
                    toastError(getErrorText(error.response.status));
                    break;
            }
            return Promise.reject(error);
        }
    );

    function getNavigationTarget() {
        if (cacType === CAC_TYPE.VISITS) {
            return '/visits';
        } else if (cacType === CAC_TYPE.CROP || cacType === CAC_TYPE.NURSERY) {
            return '/techforms';
        }
    }

    React.useEffect(() => {
        // navigate to the right section when the cacType changes
        const navigation = getNavigationTarget();
        if (!window.location.href.includes(navigation)) {
            props.history.push(navigation);
        }
    }, [cacType]);

    const onCacTypeChange = (type) => {
        if (type != null) {
            setCacType(type);
        }
    };

    return (
        <div className='App'>
            <QueryClientProvider client={queryClient}>
                <AppContext.Provider
                    value={{
                        cacType,
                        onCacTypeChange,
                        filtersByUser,
                        setFiltersByUser,
                        clearCache
                    }}>
                    <Router history={props.history}>
                        <Switch>
                            <OnlyNotLoggedRoute exact path='/' component={Login} />
                            <Route exact path='/login' render={() => <Redirect to='/' />} />
                            <OnlyNotLoggedRoute exact path='/setup-2fa' component={TwoFactorSetup} />
                            <OnlyNotLoggedRoute exact path='/secret-key-2fa' component={TwoFactorSecretKey} />
                            <OnlyNotLoggedRoute exact path='/verify-2fa' component={TwoFactorVerify} />
                            <Route
                                exact
                                path='/nav'
                                render={() => <Redirect to={getNavigationTarget()} />}
                            />
                            <PrivateRoute exact path='/techforms' component={TechFormList} />
                            <PrivateRoute
                                exact
                                path='/techforms/:techformId'
                                component={TechFormDetail}
                            />
                            <PrivateRoute
                                exact
                                path='/reports/extra'
                                component={GenericCrAlertList}
                            />
                            <PrivateRoute exact path='/visits' component={VisitList} />
                            <PrivateRoute exact path='/visits/create' component={VisitEditing} />
                            <PrivateRoute
                                exact
                                path='/visits/edit/:visitId'
                                component={VisitEditing}
                            />
                            <PrivateRoute exact path='/resources' component={ResourceList} />
                            <AdminRoute exact path='/resources/add' component={ResourceForm} />
                            <AdminRoute exact path='/resources/edit/:id' component={ResourceForm} />
                            <AdminRoute exact path='/controlpanel' component={ControlPanel} />
                            <AdminRoute exact path='/adduser' component={AddUser} />
                            <AdminRoute exact path='/manageusers' component={ManageUsers} />
                            <AdminRoute exact path='/alertparameters' component={AlertParameters} />
                            <AdminRoute exact path='/customerview' component={CustomerView} />
                            <AdminRoute exact path='/reportdates' component={CustomerReportDates} />
                            <AdminRoute exact path='/pushmessages' component={SendPushMessages} />
                            <AdminRoute exact path='/alertemail' component={CrAlertEmails} />
                            <AdminRoute exact path='/diseaseslist' component={DiseasesList} />
                            <Route exact path='/error' component={ErrorPage} />
                            <Route component={ErrorPage} />
                        </Switch>
                    </Router>
                </AppContext.Provider>
            </QueryClientProvider>
        </div>
    );
}

export default withRouter(App);
